import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "../../api/axios";
import "./LocalLandingPage.css";
import NotFoundPage from "../NotFoundPage/NotFoundPage";

import { useLocation } from "react-router-dom";


import quote1 from "./../../assets/images/quote1.webp";



const LocalLandingPage = () => {
  const [areaData, setAreaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [images, setImages] = useState([]);

  const location = useLocation();
  const match = location.pathname.match(/^\/gutter-installation-in-(.+)$/);
  const slug = match ? match[1] : null;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await axios.get(`/api/public/service-areas/${slug}`);
        if (data?.success && data.data) {
          const d = data.data;
          setAreaData({
            ...d,
            featured_review_ids: Array.isArray(d.featured_review_ids)
              ? d.featured_review_ids
              : JSON.parse(d.featured_review_ids || "[]"),
            featured_image_ids: Array.isArray(d.featured_image_ids)
              ? d.featured_image_ids
              : JSON.parse(d.featured_image_ids || "[]"),
          });
        }
      } catch (err) {
        console.error("🔥 Error fetching local page:", err);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [slug]);
  
    useEffect(() => {
    const fetchExtras = async () => {
        try {
        const [reviewsRes, imagesRes] = await Promise.all([
            axios.get("/api/public/reviews"),
            axios.get("/api/public/gallery/images"),
        ]);

        setReviews(reviewsRes.data.reviews || []);
        setImages(imagesRes.data.images || []);
        } catch (err) {
        console.error("🔥 Error fetching extras:", err);
        }
    };

    fetchExtras();
    }, []);

  if (loading) {
    return (
      <div className="local-loading-wrapper">
        <div className="local-loading">
          <div className="loading-spinner" />
          <p>Loading your local page...</p>
        </div>
      </div>
    );
  }

  
  if (!slug) return <NotFoundPage />;

  if (notFound || !areaData) {
    return (
      <NotFoundPage />
    );
  }


// --- Derive prioritized reviews ---
const localReviews = reviews.filter(
  (r) => r.service_area_id === areaData.service_area_id
);

const featuredReviews = (areaData.featured_review_ids || [])
  .map((id) => reviews.find((r) => r.id === id))
  .filter(Boolean);

const desiredReviewCount = 3;
const minLocalForLocalTitle = 1;

// 1️⃣ Start with featured (always pinned)
let displayReviews = [...featuredReviews];

// 2️⃣ Add local reviews (avoid duplicates)
displayReviews.push(
  ...localReviews.filter((r) => !displayReviews.find((d) => d.id === r.id))
);

// 3️⃣ If still not enough, fill with fallback (generic/global)
if (displayReviews.length < desiredReviewCount) {
  const fallbackReviews = reviews.filter(
    (r) =>
      !displayReviews.find((d) => d.id === r.id) &&
      r.service_area_id !== areaData.service_area_id
  );
  displayReviews.push(
    ...fallbackReviews.slice(0, desiredReviewCount - displayReviews.length)
  );
}

// 4️⃣ Limit total
displayReviews = displayReviews.slice(0, desiredReviewCount);





    // --- Derive prioritized images ---
    const localImages = images.filter(
    (img) => img.service_area_id === areaData.service_area_id
    );
    const featuredImages = (areaData.featured_image_ids || [])
    .map((id) => images.find((img) => img.id === id))
    .filter(Boolean);

const desiredImageCount = 6;
const minLocalForOwnWork = 2;
const sufficientLocalCount = 4;

// 1️⃣ Featured
let displayImages = [...featuredImages];

// 2️⃣ Add local images (avoid duplicates)
displayImages.push(
  ...localImages.filter((img) => !displayImages.find((d) => d.id === img.id))
);

// 3️⃣ Add global fallback only if needed
if (localImages.length && localImages.length < sufficientLocalCount) {
  const globalFallback = images.filter(
    (img) =>
      img.service_area_id !== areaData.service_area_id &&
      !displayImages.find((d) => d.id === img.id)
  );
  displayImages.push(
    ...globalFallback.slice(0, desiredImageCount - displayImages.length)
  );
}

// 4️⃣ Limit total
displayImages = displayImages.slice(0, desiredImageCount);

// 5️⃣ Compute local ratio among shown images
const localRatio =
  localImages.filter((img) =>
    displayImages.find((d) => d.id === img.id)
  ).length / (displayImages.length || 1);




  // Fallback
  if (displayImages.length === 0) {
  displayImages = images.slice(0, 6);
  }



  const {
    city,
    state,
    meta_title,
    meta_description,
    hero_heading,
    hero_subtext,
    main_heading,
    main_subheading,
    main_paragraph,
    cta_text,
    layout_variant,
    modules_json,
  } = areaData;


      
  // fallback for modules
  const modules =
    Array.isArray(modules_json) && modules_json.length > 0
      ? modules_json
      : ["intro", "gutter_guards", "reviews", "gallery", "cta"];

  // Separate intro module from the rest (to maintain proper hierarchy)
  const introIncluded = modules.includes("intro");
  const remainingModules = modules.filter((m) => m !== "intro");

  const isHubArea = city?.toLowerCase().includes("ocala");

  const rank = areaData.area_rank || 1;
  const isTopTier = rank >= 5;   // e.g. Ocala, Houston
  const isHighTier = rank >= 4;  // key supporting areas
  const isMidTier = rank >= 3;   // medium importance

const isTier5 = rank === 5;
const isTier4 = rank === 4;
const isTier3 = rank === 3;
const isTier2 = rank === 2;
const isTier1 = rank === 1;

const shouldLinkHeroHeading = isTier4 || isTier5;
const shouldLinkServicesHeading = isTier4 || isTier5;
const shouldLinkPretitle = isTier5; // ✅ only rank 5

  
  // 5️⃣ Compute local ratio among shown
  const localReviewRatio =
    localReviews.filter((r) =>
      displayReviews.find((d) => d.id === r.id)
    ).length / (displayReviews.length || 1);

  // 6️⃣ Title logic
  const reviewTitle =
    localReviews.length >= minLocalForLocalTitle && localReviewRatio >= 0.67
      ? `Customer Reviews from ${city}`
      : localReviews.length > 0
      ? `Some of Our Reviews near ${city}`
      : "Customer Reviews";


    // 6️⃣ Title logic
    const galleryTitle =
      localImages.length >= minLocalForOwnWork && localRatio >= 0.5
        ? `Our Work in ${city}, ${state}`
        : localImages.length > 0
        ? `Recent Projects near ${city}`
        : "Project Gallery";


  // Helper renderer for non-intro modules (the outer .clear-section / .cta-section blocks)
  const renderOuterModule = (module) => {
    switch (module) {
        case "services":
        return (
            <div className="solid-section" key="services">
            <div className="section-wrapper">
                <div className="content-columns-wrapper image-left">

                {/* Text Column */}
                <div className="content-column">
                    <div className="section-content">
                    {layout_variant === "B" ? (
                    <>

                        <h2 className="section-title main-highlight">
                          {shouldLinkServicesHeading ? (
                            <Link to="/gutter-guards">Gutter Guards</Link>
                          ) : (
                            "Gutter Guards"
                          )}
                        </h2>

                      
                    </>
                    ) : (
                    <>
                        <h3 className="section-pretitle">#1 Gutters in {state}</h3>
                        <h2 className="section-title main-highlight">Gutter Installation</h2>
                    </>
                    )}


                    <p>{main_paragraph}</p>
                    <Link to="/free-estimate" className="cta-button">
                    Book a Free Estimate
                    </Link>

                    <p>
                        Watkins Seamless Gutters provides professional installation, repair,
                        and maintenance services across {city}, {state}. From aluminum and copper
                        systems to gutter guards, we ensure long-lasting, clog-free protection.
                    </p>

                    <div className="services-grid">
                        {[
                        { name: "Seamless Installation", icon: "fa-water", link: "/seamless-gutters" },
                        { name: "Gutter Guards", icon: "fa-shield-alt", link: "/gutter-guards" },
                        { name: "Downspout Repair", icon: "fa-tools", link: "/repairs" },
                        { name: "Cleaning & Maintenance", icon: "fa-broom", link: "/cleaning" },
                        ].map((service) => (
                        <Link to={service.link} key={service.name} className="service-card">
                            <i className={`fa ${service.icon}`}></i>
                            <h4>{service.name}</h4>
                        </Link>
                        ))}
                    </div>

                    <Link to="/free-estimate" className="cta-button">
                        Book a Free Estimate 
                    </Link>
                    </div>
                </div>
                </div>
            </div>
            </div>
        );

    case "gutter_guards":
      return (
        <div className="solid-section gutter-guards-section" key="gutter_guards">
          <div className="section-wrapper">
            <div className="content-columns-wrapper image-right">

              {/* Text Column */}
              <div className="content-column">
                <div className="section-content">
                  {layout_variant === "B" ? (
                    <>
                      <h2 className="section-pretitle">Premium Protection for Florida Homes</h2>
                      <h3 className="section-title main-highlight">
                        {isHighTier ? (
                          <Link to="/gutter-guards">Gutter Guards</Link>
                        ) : (
                          "Gutter Guards"
                        )}
                      </h3>
                    </>
                  ) : (
                    <>
                      <h3 className="section-pretitle">Premium Protection for Florida Homes</h3>
                      <h2 className="section-title main-highlight">
                        {isHighTier ? (
                          <Link to="/gutter-guards">Gutter Guards</Link>
                        ) : (
                          "Gutter Guards"
                        )}
                      </h2>
                    </>
                  )}

                  <p>
                    Keep your gutters free from clogs and debris with durable
                    <Link to="/gutter-guards"> gutter guard systems</Link> installed by
                    Watkins Seamless Gutters. Ideal for homes across {city}, {state}.
                  </p>

                  <Link to="/free-estimate" className="cta-button">Request a Free Quote</Link>
                </div>
              </div>

              {/* Image Column */}
              <div className="section-image-container">
                <div className="section-image">
                  <img
                    alt={`Gutter Guards installed by Watkins Seamless Gutters in ${city}, ${state}`}
                    width="400"
                    height="300"
                    loading="lazy"
                    className={`gutter-guard-image layout-${layout_variant}`}
                    data-analytics-id={`gutter-guard-image-${layout_variant}`}
                    data-rank={rank}
                  />
                  <div className="image-badge">Clog-Free Guarantee</div>
                </div>
              </div>

            </div>
          </div>
        </div>
      );


    // === REVIEWS ===
    case "reviews":
      return (
        <div className="clear-section reviews-section" key="reviews">
          <div className="section-wrapper">
            <div className="content-columns-wrapper image-left">
              <div className="content-column">
                <div className="section-content">
                  {layout_variant === "B" ? (
                    <>
                      <h2 className="section-pretitle">Trusted by Homeowners</h2>
                      <h3 className="section-title">{reviewTitle}</h3>
                    </>
                  ) : (
                    <>
                      <h3 className="section-pretitle">Trusted by Homeowners</h3>
                      <h2 className="section-title">{reviewTitle}</h2>
                    </>
                  )}

                  <div className="reviews-container">
                    {displayReviews.length > 0 ? (
                      displayReviews.map((r) => (
                        <blockquote key={r.id} className="review-block">
                          <p>“{r.review_text}”</p>
                          <footer>
                            — {r.reviewer_name || "Customer"}
                            {r.city ? `, ${r.city}` : ""}
                          </footer>
                        </blockquote>
                      ))
                    ) : (
                      <p>No reviews available for this area yet.</p>
                    )}
                  </div>

                  <Link to="/testimonials" className="link-inline secondary">
                    Read More Reviews <i className="fa-solid fa-arrow-right"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      );



    // === GALLERY ===
    case "gallery":
      return (
        <div className="solid-section gallery-section" key="gallery">
          <div className="section-wrapper">
            <div className="content-columns-wrapper image-right">
              <div className="content-column">
                <div className="section-content">
                  {layout_variant === "B" ? (
                    <>
                      <h2 className="section-pretitle">See Our Work</h2>
                      <h3 className="section-title">{galleryTitle}</h3>
                    </>
                  ) : (
                    <>
                      <h3 className="section-pretitle">See Our Work</h3>
                      <h2 className="section-title">{galleryTitle}</h2>
                    </>
                  )}

                  <div className="gallery-grid">
                    {displayImages.length > 0 ? (
                      displayImages.map((img) => (
                        <img
                          key={img.id}
                          src={`${process.env.REACT_APP_API_URL}/uploads/gallery/${img.image_url}`}
                          alt={img.alt_text || "Project image"}
                          className="gallery-thumb"
                          loading="lazy"
                          width="240"
                          height="180"
                        />
                      ))
                    ) : (
                      <p>No gallery images available for this area yet.</p>
                    )}
                  </div>

                  <br />
                  <Link to="/gallery" className="link-inline secondary">
                    View Full Gallery <i className="fa-solid fa-arrow-right"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      );




    // === CTA ===
    case "cta":
      return (
        <div className="clear-section" key="cta">
          <div className="cta-content-wrapper">
            <div className="cta-content">
              <h2 className="cta-title">
                {cta_text || "Get a Free Gutter Quote Today"}
              </h2>
              <p className="cta-subtext">
                Watkins Seamless Gutters proudly serves homeowners in {city}, {state} — offering durable, maintenance-free gutter systems built to last. Call now or request your free estimate below to get started.
              </p>
              <div className="cta-buttons">
                <a href="tel:352-812-0831" className="cta-button primary">
                  📞 Call Now: (352) 812-0831
                </a>
                <Link to="/free-estimate" className="cta-button secondary">
                  Get Your Free Estimate
                </Link>
              </div>
            </div>
          </div>
        </div>
      );



      default:
        return null;
    }
  };

  return (
    <>
      {/* ✅ SEO Head */}
      <Helmet>
        {/* === Dynamic Title & Description === */}
        <title>
          {meta_title ||
            `${main_heading || "Gutter Installation"} in ${city}, ${state} | Watkins Seamless Gutters`}
        </title>

        <meta
          name="description"
          content={
            meta_description ||
            `Watkins Seamless Gutters provides professional ${
              main_heading?.toLowerCase() || "gutter installation"
            } and protection services in ${city}, ${state}. Call us today at 352-812-0831 for a free quote.`
          }
        />

        {/* === Open Graph / Twitter Metadata === */}
        <meta name="twitter:card" content="summary" />
        <meta
          name="twitter:title"
          content={`${
            main_heading || "Gutter Installation"
          } in ${city}, ${state} | Watkins Seamless Gutters`}
        />
        <meta
          name="twitter:description"
          content={
            meta_description ||
            `Watkins Seamless Gutters offers seamless gutters, gutter guards, and repair services in ${city}, ${state}. Call 352-812-0831.`
          }
        />

        <meta
          property="og:title"
          content={`${
            main_heading || "Gutter Installation"
          } in ${city}, ${state} | Watkins Seamless Gutters`}
        />
        <meta
          property="og:description"
          content={
            meta_description ||
            `Professional gutter installation and protection services in ${city}, ${state}. Call Watkins Seamless Gutters today at 352-812-0831.`
          }
        />

        <link rel="canonical" href={`https://www.watkinsgutters.com/service-areas/${slug}`} />

        {/* === LocalBusiness + Service Schema === */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": main_heading || "Gutter Installation",
            "provider": {
              "@type": "LocalBusiness",
              "name": "Watkins Seamless Gutters",
              "url": `https://www.watkinsgutters.com/service-areas/${slug}`,
              "telephone": "352-812-0831",
              "email": "GuttersByWatkins@gmail.com",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": city,
                "addressRegion": state,
                "addressCountry": "US"
              },
              "areaServed": {
                "@type": "Place",
                "name": [
                  `${city}, ${state}`,
                  "Sumter County, FL",
                  "Marion County, FL",
                  "Lake County, FL",
                  "Ocala, FL",
                  "The Villages, FL",
                  "Ocklawaha, FL",
                  "Summerfield, FL",
                  "Leesburg, FL"
                ]
              },
              "sameAs": ["https://www.facebook.com/GuttersByWatkins"],
              "contactPoint": {
                "@type": "ContactPoint",
                "telephone": "352-812-0831",
                "contactType": "Customer Service",
                "availableLanguage": ["English"],
                "areaServed": [
                  `${city}, ${state}`,
                  "Sumter County, FL",
                  "Marion County, FL",
                  "Lake County, FL",
                  "Ocala, FL",
                  "The Villages, FL"
                ]
              }
            },
            "description":
              meta_description ||
              `Watkins Seamless Gutters provides professional ${main_heading?.toLowerCase() || "gutter installation"} and protection services in ${city}, ${state}.`,
            "offers": {
              "@type": "Offer",
              "url": `https://www.watkinsgutters.com/service-areas/${slug}`,
              "category": main_heading || "Gutter Installation",
              "description": "Contact us today for a free, no-obligation estimate for gutter installation or protection."
            }
          })}
        </script>
      </Helmet>


      <div className={`page-wrapper layout-${layout_variant || "A"} rank-${rank}`}>
        <div className="page-content-container">
          {/* === MAIN CONTENT AREA === */}
          <div className="content-wrapper">
            <div className="page-content">
              <div className="page-content-wrapper">
                {introIncluded && (
                  <div className="page-title-wrapper">
                    <div className="page-title">
                      <h1>{hero_heading || `Gutter Installation in ${city}, ${state}`}</h1>
                    </div>

                    {hero_subtext && (
                      <div className="page-subtitle">
                        <p>{hero_subtext}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

            {/* === IMAGE + HERO TEXT SECTION === */}
            <div className="solid-section intro-image-section">
            <div className="section-wrapper">
                <div className="content-columns-wrapper image-left">

                {/* Image Column */}
                <div className="section-image-container">
                    <div className="section-image">
                      <img
                        src={
                          displayImages[0]?.image_url
                            ? `${process.env.REACT_APP_API_URL}/uploads/gallery/${displayImages[0].image_url}`
                            : quote1
                        }
                        alt={
                          displayImages[0]?.alt_text
                            ? displayImages[0].alt_text
                            : `Gutter Installation in ${city}, ${state}`
                        }
                        width="400"
                        height="300"
                        loading="lazy"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = quote1;
                        }}
                      />
                      <div className="image-badge">
                        <i className="fa-solid fa-shield-halved"></i>
                        <span>Lifetime Warranty</span> 
                      </div>
                    </div>
                </div>

                {/* Text Column */}
                <div className="content-column">
                    <div className="section-content">
                      {layout_variant === "B" ? (
                        <>
                          <h2 className="section-pretitle large-pretitle">
                            {shouldLinkPretitle ? (
                              <Link to={`/seamless-gutters`} className="linked-pretitle">
                                {main_subheading || `#1 Gutters in ${state}`}
                              </Link>
                            ) : (
                              <>{main_subheading || `#1 Gutters in ${state}`}</>
                            )}
                          </h2>

                          {shouldLinkHeroHeading ? (
                            <h3 className="section-title main-highlight linked-title">
                              <Link to="/seamless-gutters">
                                {main_heading || "Gutter Installation"}
                              </Link>
                            </h3>
                          ) : (
                            <h3 className="section-title main-highlight">
                              {main_heading || "Gutter Installation"}
                            </h3>
                          )}
                        </>
                      ) : (
                        <>
                          <h3 className="section-pretitle">
                            {shouldLinkPretitle ? (
                              <Link to={`/seamless-gutters`} className="linked-pretitle">
                                {main_subheading || `#1 Gutters in ${state}`}
                              </Link>
                            ) : (
                              <>{main_subheading || `#1 Gutters in ${state}`}</>
                            )}
                          </h3>

                          {shouldLinkHeroHeading ? (
                            <h2 className="section-title main-highlight linked-title">
                              <Link to="/seamless-gutters">
                                {main_heading || "Gutter Installation"}
                              </Link>
                            </h2>
                          ) : (
                            <h2 className="section-title main-highlight">
                              {main_heading || "Gutter Installation"}
                            </h2>
                          )}

                        </>
                      )}


                    <p>{main_paragraph}</p>
                    <Link to="/free-estimate" className="cta-button">
                        Book a Free Estimate &nbsp;<i className="fa-solid fa-arrows-turn-right"></i>
                    </Link>
                    </div>
                </div>
                </div>
            </div>
            </div>



          {/* === REMAINING SECTIONS === */}
          {remainingModules.map((m) => renderOuterModule(m))}
        </div>
      </div>
    </>
  );
};

export default LocalLandingPage;
